import { format, parseISO } from 'date-fns';

export function getBookingCalendarDateStr(booking) {
  const raw = booking?.date || booking?.bookingDate || booking?.booking_date;
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = parseISO(s);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'yyyy-MM-dd');
}

function padTimePart(n) {
  return String(n).padStart(2, '0');
}

export function parseBookingTimeRange(booking) {
  if (booking?.slotStartTime && booking?.slotEndTime) {
    const clip = (t) => String(t).slice(0, 5);
    const startClock = clip(booking.slotStartTime).split(':').map((x) => parseInt(x, 10));
    const endClock = clip(booking.slotEndTime).split(':').map((x) => parseInt(x, 10));
    const [sh, sm] = startClock;
    const [eh, em] = endClock;
    if (Number.isFinite(sh) && Number.isFinite(sm)) {
      return {
        startH: sh,
        startM: sm,
        endH: Number.isFinite(eh) ? eh : sh + 2,
        endM: Number.isFinite(em) ? em : sm,
      };
    }
  }
  const ts = booking?.timeSlot || booking?.time_slot || booking?.slotName || '';
  const times = String(ts).match(/\d{1,2}:\d{2}/g);
  if (!times || times.length < 2) return null;
  const [sh, sm] = times[0].split(':').map((x) => parseInt(x, 10));
  const [eh, em] = times[1].split(':').map((x) => parseInt(x, 10));
  if (!Number.isFinite(sh) || !Number.isFinite(sm)) return null;
  return {
    startH: sh,
    startM: sm,
    endH: Number.isFinite(eh) ? eh : sh + 2,
    endM: Number.isFinite(em) ? em : sm,
  };
}

export function formatHm(h, m) {
  return `${padTimePart(h)}:${padTimePart(m)}`;
}

/** Format a number as INR (e.g. ₹7,500). */
export function formatInr(n) {
  return `\u20B9${(Number(n) || 0).toLocaleString('en-IN')}`;
}

/** Time-based schedule badge for today's bookings (Ongoing / Upcoming / Completed). */
export function getLiveScheduleStatus(booking) {
  const { label } = getBookingStatus(booking);
  if (label === 'Ongoing') return { status: 'booked', label: 'Ongoing' };
  if (label === 'Upcoming') return { status: 'booked', label: 'Upcoming' };
  return { status: 'completed', label: 'Completed' };
}

export function getBookingStatus(booking) {
  const cal = getBookingCalendarDateStr(booking);
  const dayBase = cal && !Number.isNaN(parseISO(`${cal}T12:00:00`).getTime())
    ? parseISO(`${cal}T12:00:00`)
    : new Date();
  const tr = parseBookingTimeRange(booking);
  const slotStart = new Date(dayBase);
  const slotEnd = new Date(dayBase);
  if (tr) {
    slotStart.setHours(tr.startH, tr.startM, 0, 0);
    slotEnd.setHours(tr.endH, tr.endM, 0, 0);
  } else {
    slotStart.setHours(0, 0, 0, 0);
    slotEnd.setHours(23, 59, 59, 999);
  }

  const now = new Date();
  if (now >= slotStart && now <= slotEnd) {
    return { label: 'Ongoing', color: 'bg-blue-100 text-blue-800' };
  }
  if (now < slotStart) {
    return { label: 'Upcoming', color: 'bg-green-100 text-green-800' };
  }
  return { label: 'Completed', color: 'bg-gray-100 text-gray-800' };
}

/** True when the booking's calendar date is strictly after todayStr (yyyy-MM-dd). */
export function isUpcomingAfterToday(booking, todayStr) {
  if (!todayStr || booking?.status === 'cancelled') return false;
  const cal = getBookingCalendarDateStr(booking);
  return Boolean(cal && cal > todayStr);
}

export function getPaymentStatus(booking) {
  const total = parseFloat(booking.totalAmount) || parseFloat(booking.price) || 0;
  const advance = parseFloat(booking.advanceAmount) || 0;
  const balance = total - advance;

  if (balance <= 0) {
    return { label: 'Confirmed', color: 'bg-green-100 text-green-800' };
  }
  if (advance > 0) {
    return { label: 'Pending', color: 'bg-amber-100 text-amber-800' };
  }
  return { label: 'Pending', color: 'bg-red-100 text-red-800' };
}
